import { Inject } from '@odinjs/odin';
import { ActionAPI, BaseModalProps, ButtonAPI, ContextualBarAPI, DeckAPI, ModalButton, ModalService, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';

import { HealthProfessionalUser } from '../../../../app/authentication';
import { FragmentNames, MedicationViewerAliases } from '../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { AdminType, OptionCheck } from '../../../../constants/infusion-solutions';
import { ApplicationRoute } from '../../../../constants/routes';
import { YesNoType } from '../../../../utils/enum-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { MedicationAndDateHeaderParams } from '../../../commons/header/medication-and-date-header-params';
import { ScheduleMedicationParams } from '../../../schedule-medication-infusion/schedule-medication-infusion-params';
import { AdminModalsService } from '../../services/admin-modals-service';
import MedicationService from '../../services/medication-service';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../services/timeline-service';
import { ChemotherapyMedicationType, ChemotherapyStatus, Params, PrescriptionStatus } from '../administration-schedule/administration-schedule-types';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { Function1113Param13Values } from '../oral-supplements-viewer/oral-supplements-params';
import { ConsistAdministrationMarginInfo } from '../recommendation-orders/consist-administration-margin-info';
import { MedicationViewerParams } from './medication-viewer-params';
import { AdminScheduleType, BodyCheckInfo, FirstMedicationCheckInfoResponse, PatientProfile, ProcessSequence, RefusalConfirmationType, ResponseShow, Status } from './medication-viewer-types';

import './medication-viewer-controller.css';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';

const CLINICIAN_APPLICATION = 'C';
const ONLY_START = 'SI';

@Controller({ component: MedicationViewerAliases.MEDICATION_VIEWER_LAYOUT, fragment: FragmentNames.MEDICATION_VIEWER })
class MedicationViewerController extends BaseController {

  @Inject(MedicationViewerAliases.MEDICATION_VIEWER_CONTEXTUAL_BAR_ACTION)
  private medicationViewerRefusalAction!: ActionAPI;

  @Inject(MedicationViewerAliases.SCHEDULE_MEDICATION)
  private medicationViewerScheduleMedication!: ActionAPI;

  @Inject(MedicationViewerAliases.ADMINISTRATION_BY_SCHEDULE_ACTION)
  private administrationByScheduleActionApi!: ActionAPI;

  @Inject(MedicationViewerAliases.MEDICATION_VIEWER_BUTTON)
  private medicationViewerButtonApi!: ButtonAPI;

  @Inject(MedicationViewerAliases.MEDICATION_VIEWER_DECK)
  private medicationViewerDeckApi!: DeckAPI;

  @Inject(MedicationViewerAliases.MEDICATION_VIEWER_PANEL_BUTTON)
  private medicationViewerPanelButtonApi!: PanelAPI;

  @Inject(MedicationViewerAliases.MEDICATION_VIEWER_CONTENT_PANEL)
  private medicationViewerContentPanelApi!: PanelAPI;

  redirectGeneratePatientRefusal(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = `${ApplicationRoute.ELECTRONIC_MEDICATION_ADMINISTRATION}${ApplicationRoute.SEACH_PATIENT_EMAR}${ApplicationRoute.ADEP_GENERATE_PATIENT_REFUSAL}`

    const data = MedicineAndSolutionsParams?.params?.startId ? new Date(MedicineAndSolutionsParams.params.startId).toLocaleString() : '';

    MedicationViewerParams.params = {
      eventDate: data,
      hourSequence: MedicineAndSolutionsParams.params?.hourSequence,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      itemType: (MedicineAndSolutionsParams.params?.ieTypeItem as PrescriptionType) ?? PrescriptionType.MEDICINE,
      medicationName: MedicineAndSolutionsParams.params.dsMaterialId,
      prescriptionNumber: String(MedicationViewerParams.adminSchedule.prescriptionNumber),
    }

    applicationContext.redirect(redirectRoute, {
      encounterId,
      patientId,
    });
  }

  redirectToSuspendMedication(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    applicationContext.redirect(ApplicationRoute.ADEP_MEDICATION_SUSPEND, {
      encounterId,
      patientId,
    });
  }

  validatePatientRefusalIsVisible(): void {
    const ieStatus = MedicineAndSolutionsParams.params.ieStatus;
    const paramTasyOrTws387 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_TO_RECORD_PATIENT_REFUSAL);

    const hideRefusalActionStatusList = [
      'ADMINISTERED',
      'STARTED',
      'PENDING_REFUSAL',
      'REFUSED_ADMINISTERED'
    ];

    const showRefusionAction = !hideRefusalActionStatusList.some(str => str === ieStatus);

    if (paramTasyOrTws387 === Params.ALLOWED) {
      if (showRefusionAction) {
        this.medicationViewerRefusalAction.setEnabled(true);
      }
    }

    const paramTasyOrTws29 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_RESCHEDULE_MEDICATION);

    const { encounterId } = this.historyService.getCurrentRouteParams();

    const data = MedicineAndSolutionsParams?.params?.startId ? MedicineAndSolutionsParams.params.startId : '';

    const body = {
      itemNumber: MedicineAndSolutionsParams.params.nrSequenceId,
      itemType: 'M',
      prescriptionNumber: encounterId,
      time: new Date(data).toISOString(),
      timeNumber: MedicineAndSolutionsParams.params.hourSequence,
    } as Record<string, Value>;

    HttpService.post(
      Endpoint.SHOW_SCHEDULE_OPTION, {}, { body }
    ).then(response => {
      const resp = response.body as ResponseShow;
      const positiveStatus = 'S';

      const prescriptionValidityDate = moment(MedicineAndSolutionsParams.params.prescriptionValidityDate, 'DD/MM/YYYY hh:mm:ss');

      const isBeforeValidateDate = moment().isBefore(prescriptionValidityDate);
      
      if (paramTasyOrTws29 === Params.ALLOWED && resp.status !== positiveStatus && isBeforeValidateDate) {
        this.medicationViewerScheduleMedication.setEnabled(true);
      }
    }).catch((err) => {
      if (err.body) {
        const message = err.body.message;
        this.showToastErrorShowOption(message);
      }
    });
  }

  showToast(message: string, typeMessage: ToastLevel): void {
    const toastTime = 5000;

    ToastService.open({
      dismissExpression: message,
      dismissible: true,
      highlight: false,
      id: 'generate-patient-refusal',
      level: typeMessage,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      type: ToastType.BANNER,
    });

    setTimeout(() => {
      ToastService.close('generate-patient-refusal');
    }, toastTime);
  }

  shouldShowSuspendMedicationAction(): boolean {
    const allowNonMedicalSuspendOrReverseSuspension = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_NON_MEDICAL_SUSPEND_OR_REVERSE_SUSPENSIONS);
    const allowMedicalUsersSuspendSchedule = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_MEDICAL_USERS_SUSPEND_SCHEDULES);
    const user = foundationContext.getUser() as HealthProfessionalUser;

    if (user.isDoctor) {
      return allowMedicalUsersSuspendSchedule !== YesNoType.NO;
    }
    return allowNonMedicalSuspendOrReverseSuspension !== YesNoType.NO;
  }

  showFunctionAdministrationByProcessOrBySchedule(adminType: string): boolean {
    const param8 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.HOW_TO_ADMINISTER_THE_MEDICATION);
    const { status } = this.historyService.getCurrentRouteParams();
    const isPreparationStatus = status === 'PREPARATION';
    const hasStatusOriginP = this.context.get('responseBody')?.statusOrigin === 'P';
    const isPendingRefusal = status === 'PENDING_REFUSAL';
    const checkSchedule = (adminType === 'schedule') && (param8 === AdminType.PROCESS_OR_SCHEDULE && (isPreparationStatus || (hasStatusOriginP && isPendingRefusal)));
    return checkSchedule;
  }

  getBodyCheckInfo(): BodyCheckInfo {
    const timelineEvent = TimelineContext.get() as TimeLineEventMedication;
    const option = OptionCheck.FIRST_CHECK;
    const userName = foundationContext.getUser()?.username || '';

    return {
      itemCode: Number(timelineEvent.itemCode),
      itemType: timelineEvent.itemType,
      option,
      prescriptionNumber: String(MedicationViewerParams.adminSchedule.prescriptionNumber) ?? String(MedicationViewerParams.chemotherapy.prescriptionNumber),
      scheduleSequence: Number(timelineEvent.id),
      userName,
    }
  }

  validateStatusMedication(): Promise<boolean> {
    const timelineEvent = TimelineContext.get();
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);

    if (timelineEvent.itemType === 'M') {
      const timeLineEventMedication = timelineEvent as TimeLineEventMedication;
      const option = 'MPC';
      const userName = foundationContext.getUser()?.username;

      const body = {
        itemCode: Number(timeLineEventMedication.itemCode),
        itemType: timeLineEventMedication.itemType,
        option,
        prescriptionNumber: String(MedicationViewerParams.adminSchedule.prescriptionNumber) ?? String(MedicationViewerParams.chemotherapy.prescriptionNumber),
        scheduleSequence: Number(timeLineEventMedication.id),
        userName,
      }

      return HttpService.post(Endpoint.EMAR_GET_INFO_FIRST_MEDICATION_CHECK, {}, { body })
        .then(response => {
          const responseBody = response.body as FirstMedicationCheckInfoResponse;
          if (!responseBody?.responseDescription || param561 === Params.NOT_ALLOWED) {
            return false;
          }
          return true;
        });
    }
    return Promise.resolve(true);
  }

  async findMedicationInformation(): Promise<void> {
    const param713 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.USE_GEDIPA_FUNCTION_STATUS_THERAPEUTIC_PLAN_TAB);
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    const response = await HttpService.get(Endpoint.EMAR_FIND_MEDICATION_INFORMATION, {
      gedipaStatus: param713,
      scheduleSequenceNumber,
    })

    const responseBody = response.body as Record<string, Value>;
    this.context.set({ responseBody });
  }

  checkUrgencyMedication(isUrgency: string, status: string): void {
    if (isUrgency === YesNoType.YES && status !== Status.ADMINISTERED) {
      this.showToastUrgencyMedication();
    }
  }

  validateIsExistProcessNumberAndShowActionProcess(): void {
    const { scheduleSequenceNumber, status } = this.historyService.getCurrentRouteParams();

    if (status !== Status.ADMINISTERED) {
      HttpService.get(Endpoint.EMAR_GET_NUMBER_PROCESS, { tagNumber: Number(scheduleSequenceNumber) })
        .then(res => {
          const response = res.body as Record<string, Value>[] as ProcessSequence;
          const processSequence = response?.processSequence;

          this.context.set({ processSequence });
        });
    }
  }

  private adjustButtonChemoStatus(chemotherapyStatus: string | undefined, buttonText: string, verifyStatusChemo: boolean): void {
    this.medicationViewerPanelButtonApi.setVisible(true);
    switch (chemotherapyStatus) {
      case ChemotherapyStatus.STARTED: this.medicationViewerButtonApi.setValue('text.finish');
        break;
      case ChemotherapyStatus.PENDING:
        if (verifyStatusChemo) {
          this.medicationViewerButtonApi.setValue('tws.emar.perform-first-check');
        } else {
          this.medicationViewerButtonApi.setValue(buttonText);
        }
        break;
      case ChemotherapyStatus.ADMINISTERED: this.medicationViewerPanelButtonApi.setVisible(false);
        break;
      case ChemotherapyStatus.SUSPENDED: this.medicationViewerPanelButtonApi.setVisible(false);
        return;
      case ChemotherapyStatus.CHECKED_FIRST:
        this.medicationViewerButtonApi.setValue(buttonText);
        break;
    }
    this.context.set({ processOrSchedule: 'medication-admin-schedule' });
  }

  async validatesTheTextOfTheAdministrationButtonChemotherapy(): Promise<void> {
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;
    const param23 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOW_CHECK_TO_START_AND_ALLOW_CHECK_TO_FINISH);
    const buttonText = param23 !== Params.NOT_ALLOWED ? 'text.start-chemotherapy' : 'text.administration';
    const isFirstChecked = await MedicationService.validateStatusItem(this.getBodyCheckInfo());
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);
    const verifyStatusChemo = isFirstChecked && param561 === Params.ALLOWED && chemotherapyStatus === ChemotherapyStatus.PENDING;
    this.adjustButtonChemoStatus(chemotherapyStatus, buttonText, verifyStatusChemo);
  }

  private setButtonText(firstCheck: boolean, adminByProcess: boolean, adminBySchedule: boolean): void {
    if (adminByProcess) {
        this.medicationViewerButtonApi.setValue('text.administration-by-process');
        this.context.set({ processOrSchedule: 'medication-admin-process' });
        return;
    }

    if (firstCheck) {
      this.medicationViewerButtonApi.setValue('tws.emar.perform-first-check');
      this.context.set({ processOrSchedule: 'medication-admin-schedule' });
      return;
    }

    if (adminBySchedule) {
      this.medicationViewerButtonApi.setValue('text.administration-by-schedule');
      this.context.set({ processOrSchedule: 'medication-admin-schedule' });
    }
  }

  async validatesTheTextOfTheAdministrationButton(): Promise<void> {
    const param3 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const param8 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.HOW_TO_ADMINISTER_THE_MEDICATION);
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);
    const { status } = this.historyService.getCurrentRouteParams();
    const isFirstChecked = await MedicationService.validateStatusItem(this.getBodyCheckInfo());

    const firstCheck = param561 === Params.ALLOWED && isFirstChecked
      && (param8 !== AdminType.PROCESS || param3 === Params.NOT_ALLOWED);
    const isPreparationStatus = ['PREPARATION', 'CHECKED'].includes(status as string);
    const hasStatusOriginP = this.context.get('responseBody')?.statusOrigin === 'P';
    const isPendingRefusal = status === 'PENDING_REFUSAL';
    const adminByProcess = (param8 === AdminType.PROCESS) ||
     (param8 === AdminType.PROCESS_OR_SCHEDULE && (isPreparationStatus || (hasStatusOriginP && isPendingRefusal)));
    const adminByScheduleFirst = param8 === AdminType.PROCESS_OR_SCHEDULE && status !== Status.PREPARATION && this.isPreMedication();
    const adminByScheduleSecond = param8 === AdminType.SCHEDULE;
    const adminBySchedule = adminByScheduleFirst || adminByScheduleSecond;

    if (this.medicationViewerPanelButtonApi.isVisible()) {
      this.setButtonText(firstCheck, adminByProcess, adminBySchedule);
    }
  }

  private isPreMedication(): boolean {
    const isChemotherapy = MedicationViewerParams.chemotherapy.isChemotherapy;
    const isMedication = MedicationViewerParams.chemotherapy.isMedication;
    return !isChemotherapy || isMedication !== ChemotherapyMedicationType.MEDICATION;
  }

  private buildRefusalConfirmationBody(): RefusalConfirmationType {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    return {
      encounterNumber: encounterId as number,
      eventDate: moment().format(ISO_FULL_DATE_FORMAT),
      hourSequence: MedicineAndSolutionsParams.params?.hourSequence,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      itemType: MedicineAndSolutionsParams.params?.ieTypeItem ?? PrescriptionType.MEDICINE,
      justificationDescription: '',
      prescriptionNumber: String(MedicationViewerParams.adminSchedule.prescriptionNumber),
      userName: foundationContext.getUser()?.username,
    }
  }

  private async patientMedicationRefusalConfirmation(): Promise<void> {
    const eventDate = moment().format(ISO_FULL_DATE_FORMAT);

    const validation = await ConsistAdministrationMarginInfo.validate({
      administrationDate: eventDate,
      itemType: (MedicineAndSolutionsParams.params?.ieTypeItem as PrescriptionType) ?? PrescriptionType.MEDICINE,
      justification: '',
      scheduleSequenceNumber: Number(MedicineAndSolutionsParams.params?.hourSequence ?? 0),
    });

    if (!validation.allowed) {
      this.showToast(validation.message, ToastLevel.ERROR);
      return;
    }

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    const body = this.buildRefusalConfirmationBody();
    this.medicationViewerDeckApi.setLoading(true);
    HttpService.post(Endpoint.ADEP_GENERATE_PATIENT_REFUSAL, {}, { body })
      .then(() => {
        MedicationService.showToast('text.finish-patient-refusal', ToastLevel.SUCCESS);
        applicationContext.redirect(redirectRoute, { encounterId, patientId });
        applicationContext.clearNavigationStack();
      }).catch((err) => {
        const displayError = err?.body || 'tws.generic-backend-error.call-administrator';
        MedicationService.showToast(displayError, ToastLevel.ERROR);
      }).finally(() => this.medicationViewerDeckApi.setLoading(false));
  }

  @OnMount({ target: MedicationViewerAliases.MEDICATION_VIEWER_LAYOUT })
  async handleMountLayout(): Promise<void> {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;

    this.context.set({
      encounterId,
      patientId,
    });

    await this.findMedicationInformation()
    this.medicationViewerContentPanelApi.setVisible(true)
  }

  @OnMount(MedicationViewerAliases.MEDICATION_VIEWER_CONTEXTUAL_BAR)
  handleMountContextualBar(event: MountEvent): void {
    const contextualBarApi = event.target as ContextualBarAPI
    const itemType = TimelineContext.get().itemType
    const isGlycemiaControlsAssociatedItem = itemType === PrescriptionType.GLYCEMIA_CONTROLS_ASSOCIATED_ITEMS
    const isProceduresAssociatedItems = itemType === PrescriptionType.PROCEDURES_ASSOCIATED_ITEMS
    if (isProceduresAssociatedItems) {
      contextualBarApi.setTitle(MessageService.translate('text.associated-items-with-procedures'))
    }
    if (isGlycemiaControlsAssociatedItem) {
      contextualBarApi.setTitle(MessageService.translate('text.glycemia-controls-associated-items'))
    }
  }

  @OnMount({ target: MedicationViewerAliases.MEDICATION_VIEWER_CONTEXTUAL_BAR_ACTION_SUSPEND })
  handleMountContextualBarActionSuspend(evt: MountEvent): void {
    if (this.allowOnlyConsultingEmar() || !this.shouldShowSuspendMedicationAction()) {
      return;
    }

    const statusItem = TimelineContext.get().status;
    const isChemotherapy = MedicationViewerParams.chemotherapy?.isChemotherapy;
    const statusChemotherapy = MedicineAndSolutionsParams.params.chemotherapyStatus;
    const action = evt.target as ActionAPI;
    const itemIsNotAdministered = statusItem !== Status.ADMINISTERED || (!!isChemotherapy && statusChemotherapy !== ChemotherapyStatus.ADMINISTERED)
    action.setEnabled(itemIsNotAdministered);
  }

  private allowOnlyConsultingEmar(): boolean {
    const paramTasyOrTws27 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR);
    return paramTasyOrTws27 === Params.ALLOWED
  }

  @OnMount({ target: MedicationViewerAliases.ADMINISTRATION_BY_PROCESS_ACTION })
  handleMountProcessAction(): void {
    this.validateIsExistProcessNumberAndShowActionProcess();
  }

  @OnMount({ target: MedicationViewerAliases.ADMINISTRATION_BY_SCHEDULE_ACTION })
  handleMountScheduleAction(): void {
    const adminType = 'schedule';
    const isAdministered = TimelineContext.get().status;
    const scheduleActionIsVisible = this.showFunctionAdministrationByProcessOrBySchedule(adminType);

    if (scheduleActionIsVisible && isAdministered !== Status.ADMINISTERED) {
      this.administrationByScheduleActionApi.setEnabled(true);
    }
  }

  @OnMount({ target: MedicationViewerAliases.MEDICATION_VIEWER_BUTTON })
  handleMountViewerButton(): void {
    const param13 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOWS_TO_PERFORM_PRESCRIPTION_ITEMS) as string;
    const isAdministeredChemotherapy = MedicationViewerParams.chemotherapy?.isAdministered;
    const checkParam13 = ![ChemotherapyMedicationType.RESIDENTIAL_USE as string, ChemotherapyMedicationType.PRE_MEDICATION as string].includes(param13);
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;

    if (this.allowOnlyConsultingEmar()) {
      this.medicationViewerButtonApi.setVisible(false);
      return;
    }

    if (this.isAdministered()) {
      if (isAdministeredChemotherapy === ChemotherapyStatus.STARTED || chemotherapyStatus === ChemotherapyStatus.CHECKED_FIRST) {
        return;
      }
      this.medicationViewerPanelButtonApi.setVisible(false);
    }

    if (checkParam13) {
      this.medicationViewerPanelButtonApi.setVisible(false);
    }
  }

  private isAdministered(): boolean {
    const itemStatus = TimelineContext.get().status;
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;
    const administeredStatus = [Status.ADMINISTERED, Status.REFUSED_ADMINISTERED, PrescriptionStatus.ADMINISTERED]
    return administeredStatus.some((status) => itemStatus === status) || chemotherapyStatus === ChemotherapyStatus.ADMINISTERED
  }

  @OnClick({ target: MedicationViewerAliases.MEDICATION_VIEWER_CONTEXTUAL_BAR_ACTION })
  handleClickContextualbarAction(): void {
    const profile = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.INFORM_OBS_WHEN_REFUSAL_MEDICATION);
    const marginAction = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN);
    const justificationRequiredByMargin = marginAction === Function1113Param13Values.REQUIRED_JUSTIFICATION
      || marginAction === Function1113Param13Values.NOT_ALLOWED_AND_jUSTIFICATION;
    if (profile === PatientProfile.NO && !justificationRequiredByMargin) {
      this.patientMedicationRefusalConfirmation();
      return;
    }
    this.redirectGeneratePatientRefusal();
  }

  @OnClick({ target: MedicationViewerAliases.MEDICATION_VIEWER_CONTEXTUAL_BAR_ACTION_SUSPEND })
  handleClickContextualbarActionSuspend(): void {
    this.redirectToSuspendMedication();
  }

  @OnMount({ target: MedicationViewerAliases.REVERSE_ADMINISTRATION })
  handleMountReverseAdministrationAction({ target }: EventData<ActionAPI>): void {
    target.setEnabled(this.isReverseActionEnabled());
  }

  @OnMount({ target: MedicationViewerAliases.MEDICATION_VIEWER_DECK })
  handleMountMedicationViewerDeck(): void {
    const responseBody = this.context.get('responseBody')
    const isDelayed = TimelineContext.get().delayed;
    this.setMedicationInformation([{...responseBody, isDelayed}]);
  }

  private setMedicationInformation(responseBody: Record<string, Value>[]): void {
    const { status } = this.historyService.getCurrentRouteParams() as { status: string };
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus as string;
    const timelineContext = TimelineContext.get() as TimeLineEventMedication;
    const medicationInfo = responseBody[0];
    const itemDoseQuantity = Number(timelineContext?.itemType === 'IAG'
      ? medicationInfo?.doseQuantity
      : medicationInfo?.dosePrescriptionQuantity);
    const statusItem = medicationInfo?.statusItem as string;
    const { dilutionDescription } = medicationInfo;
    const dilutionDescriptionFormatted = dilutionDescription ? String(medicationInfo?.dilutionDescription) : null;
    const isChemotherapy = TimelineContext.get().chemotherapy;
    const pdoPrn = Boolean(medicationInfo?.isPdoPrn) || Boolean(medicationInfo?.pdoPrn);

    MedicationViewerParams.adminSchedule = {
      dilutionDescription: dilutionDescriptionFormatted,
      dosePrescriptionQuantity: itemDoseQuantity,
      materialSequence: Number(medicationInfo?.materialSequence),
      medicationHour: String(medicationInfo?.scheduleDate),
       pdoPrn: pdoPrn,
      prescriptionNumber: Number(medicationInfo?.prescriptionNumber),
    };

    MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
      medicationName: String(medicationInfo?.materialDescription),
      scheduleDate: String(medicationInfo?.scheduleDate),
      type: AdminScheduleType.MEDICATION,
    };

    MedicationViewerParams.chemotherapy = {
      encounterIdSequence: Number(medicationInfo?.encounterIdSequence),
      groupingNumber: Number(medicationInfo?.groupingNumber),
      isAdministered: String(medicationInfo?.isAdministered || ''),
      isChemotherapy,
      isMedication: TimelineContext.get().isMedication,
      isUrgency: String(medicationInfo?.isUrgency),
      materialSequence: Number(medicationInfo?.materialSequence),
      orderSequence: Number(medicationInfo?.orderSequence),
      prescriptionNumber: Number(medicationInfo?.prescriptionNumber)
    };
    const isMedication = TimelineContext.get().isMedication;
    const isUrgency = String(medicationInfo?.isUrgency);
    this.checkUrgencyMedication(isUrgency, status);
    const data = responseBody;

    this.medicationViewerDeckApi.setData(data);
    const isMedicineProcess = ['CHECKED', 'PREPARATION'].includes(status);
    const validateChemo = !isMedicineProcess && isChemotherapy && isMedication === ChemotherapyMedicationType.MEDICATION && chemotherapyStatus !== ChemotherapyStatus.ADMINISTERED;
    const validatePatient = (status !== Status.ADMINISTERED || (status === Status.ADMINISTERED && chemotherapyStatus === PrescriptionStatus.CHECKED_FIRST))
    && statusItem !== PrescriptionStatus.ADMINISTERED;
    this.validateAdmistrationTextButton(validatePatient, validateChemo);
  }

  private validateAdmistrationTextButton(validatePatient: boolean, validateChemo: boolean): void {
    if (validatePatient) {
      this.validatePatientRefusalIsVisible();
      if (validateChemo) {
        this.validatesTheTextOfTheAdministrationButtonChemotherapy();
      } else {
        this.validatesTheTextOfTheAdministrationButton();
      }
    }
  }

  @OnClick({ target: MedicationViewerAliases.REVERSE_ADMINISTRATION })
  handleClickReverseAdministrationAction(): void {
    const reverseLastEventExpression = 'text.revert-last-event'
    const contextData = this.context.getBulkData() as unknown as Record<string, Value>
    ModalService.openDefault({
      contextData,
      focusOnOpen: false,
      fragment: FragmentNames.REVERSE_MEDICATION,
      id: FragmentNames.REVERSE_MEDICATION,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.REVERSE_MEDICATION),
      title: reverseLastEventExpression,
    })
  }

  private isReverseActionEnabled(): boolean {
    const { status } = TimelineContext.get();
    if (this.allowOnlyConsultingEmar()) {
      return false;
    }
    const adepParam6Value = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_REVERSING_TIME_MANAGEMENT);
    const canReverse = adepParam6Value === PatientProfile.YES || adepParam6Value === PatientProfile.MANDATORY_JUSTIFICATION
    const isStatusAdministered = status === Status.ADMINISTERED || status === PrescriptionStatus.ADMINISTERED
    return isStatusAdministered && canReverse
  }

  private addNewParams(): void {
    const data = MedicineAndSolutionsParams?.params?.startId ? MedicineAndSolutionsParams.params.startId : '';

    MedicationViewerParams.params = {
      eventDate: data,
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      medicationName: MedicineAndSolutionsParams.params.dsMaterialId,
      prescriptionNumber: String(MedicationViewerParams.adminSchedule.prescriptionNumber),
    }

    const { encounterId, scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    ScheduleMedicationParams.prototype.encounter = encounterId as string;
    ScheduleMedicationParams.prototype.scheduleSequenceNumber = Number(scheduleSequenceNumber);
  }

  @OnClick({ target: MedicationViewerAliases.SCHEDULE_MEDICATION })
  handleClickActionSchedule(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.addNewParams();
    const route = ApplicationRoute.EMAR_SCHEDULE_MEDICATION_INFUSION;
    applicationContext.redirect(route, { encounterId, patientId });
  }

  showToastErrorShowOption(value: string): void {
    return ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'show-option-error',
      level: ToastLevel.ERROR,
      message: value,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  showToastUrgencyMedication(): void {
    return ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'show-option-error',
      level: ToastLevel.WARNING,
      message: 'msg.urgency-medication',
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  @OnClick({ target: MedicationViewerAliases.ADMINISTRATION_BY_PROCESS_ACTION })
  handleClickAdministrationByProcess(): void {
    const processSequence = this.context.get('processSequence');
    const adminType = 'medication-admin-process';
    AdminModalsService.modalAdminByProcessOrScheduleIsVisible(adminType, { processSequence: Number(processSequence) });
  }

  @OnClick({ target: MedicationViewerAliases.ADMINISTRATION_BY_SCHEDULE_ACTION })
  handleClickAdministrationBySchedule(): void {
    const adminType = 'medication-admin-schedule';
    AdminModalsService.modalAdminByProcessOrScheduleIsVisible(adminType, {});
  }

  private clickMedButton(finishChemoMed: boolean, admByScheduleChemo: boolean, admBySchedule: boolean, admByProcess: boolean): void {
    const processSequence = this.context.get('processSequence');
    const adminType = this.context.get('processOrSchedule');

    if (finishChemoMed) {
      AdminModalsService.modalAdminByProcessOrScheduleIsVisible('finish-chemotherapy-medication', {
        medicationOrSolution: 'medication'
      });

      return;
    }

    if (admByScheduleChemo) {
      this.clickAdmByScheduleChemo();
    }

    if (admBySchedule) {
      AdminModalsService.modalAdminByProcessOrScheduleIsVisible(String(adminType), {
        medicationOrSolution: 'medication',
        processSequence: Number(processSequence)
      });
      return;
    }

    if (admByProcess) {
      this.clickAdmByProcess();
    }
  }

  private clickAdmByProcess(): void {
    const { status } = this.historyService.getCurrentRouteParams();
    const adminType = this.context.get('processOrSchedule');
    const processSequence = this.context.get('processSequence');
    const medicationName = MedicationAndDateHeaderParams.medicationAndDateHeaderProps?.medicationName;
    const errorMessage = MessageService.translate('text.the-medication-is-not-prepared');
    const errorMessageFormatted = errorMessage.replace('#@medicamento#@', String(medicationName));
    const validStatusToAdminByProccess = [Status.PREPARATION, Status.CHECKED, Status.CHECKED_FIRST];
    const hasStatusOriginP = this.context.get('responseBody')?.statusOrigin === 'P';
    const isPendingRefusal = status === 'PENDING_REFUSAL';

    const canAdminByProccess = validStatusToAdminByProccess.includes(status as Status) || (isPendingRefusal && hasStatusOriginP);

    if (canAdminByProccess) {
      AdminModalsService.modalAdminByProcessOrScheduleIsVisible(adminType, {
        medicationOrSolution: 'medication',
        processSequence: Number(processSequence),
      });
    } else {
      MedicationService.showToast(errorMessageFormatted, ToastLevel.ERROR);
      return;
    }
  }

  private clickAdmByScheduleChemo(): void {
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    const request = {
      encounterIdSequence: MedicationViewerParams.chemotherapy.encounterIdSequence,
      groupingSequence: MedicationViewerParams.chemotherapy.groupingNumber,
      isApplication: CLINICIAN_APPLICATION,
      isType: PrescriptionStatus.ADMINISTERED,
      materialSequence: MedicationViewerParams.chemotherapy.materialSequence,
      orderSequence: MedicationViewerParams.chemotherapy.orderSequence,
      prescriptionNumber: MedicationViewerParams.chemotherapy.prescriptionNumber,
    }
    MedicationService.administrationChemotherapyMedication(request)
      .then((res) => {
        MedicationService.showToast(String(res.message), ToastLevel.SUCCESS);
        AdminModalsService.redirectToTimeline('administration-by-schedule');
        applicationContext.redirect(redirectRoute, { encounterId, patientId });
        applicationContext.clearNavigationStack()
      });
  }

  @OnClick({ target: MedicationViewerAliases.MEDICATION_VIEWER_BUTTON })
  handleClickMedicatiobButton(): void {
    const adminType = this.context.get('processOrSchedule');
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;
    const param23 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOW_CHECK_TO_START_AND_ALLOW_CHECK_TO_FINISH);
    const finishChemoMed = param23 === Params.ALLOWED && chemotherapyStatus === ChemotherapyStatus.STARTED;
    const admByScheduleChemo = param23 === ONLY_START && chemotherapyStatus === ChemotherapyStatus.STARTED;
    const admBySchedule = adminType === 'medication-admin-schedule' && chemotherapyStatus !== ChemotherapyStatus.STARTED;
    const admByProcess = adminType === 'medication-admin-process';

    this.clickMedButton(finishChemoMed, admByScheduleChemo, admBySchedule, admByProcess);
  }

  private dialogModalConfirm(event: AbortableCloseEvent, id: string): void {
    event.abort();
    const buttons: ModalButton[] = [];
    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.redirectToTimeline(id),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: BaseModalProps = {
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      id: 'dialog-modal-service',
      title: 'text.message-alert-attention',
    };

    ModalService.openDialog(dialogModal);
  }

  private redirectToTimeline(id: string): void {
    ModalService.close(id);
    ModalService.close('dialog-modal-service');
  }
}

export default MedicationViewerController;
